'use client';
import { Accordion } from '@curology/ui-components-web/react/Accordion';
import { AccordionContent } from '@curology/ui-components-web/react/Accordion/AccordionContent';
import { AccordionSummary } from '@curology/ui-components-web/react/Accordion/AccordionSummary';
import { Link } from '@curology/ui-components-web/react/Link';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React, { useState } from 'react';

import { type Brand } from '@/consts/brands';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { type PhotoResource } from '@/services/funnelService/types';

import ExamplePhoto from './examplePhoto';
import PhotoUploader from './photoUploader';
import { type QuestionPhotoUploadModel } from '../../../config/questions/photoUploadQuestion';
import { type PhotoUploadFile } from '../types/types';

const PhotoUploadAccordion = ({
  brand,
  index,
  photo,
  question,
  requiredPhotoTypes,
  setShowPhotoIdModal,
  setSubmitButtonDisabled,
  setUploadedFiles,
  treatmentCategory,
  uploadedFiles,
}: {
  brand: Brand;
  index: number;
  photo: PhotoResource;
  question: QuestionPhotoUploadModel;
  requiredPhotoTypes: string[];
  setShowPhotoIdModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSubmitButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  setUploadedFiles: React.Dispatch<React.SetStateAction<PhotoUploadFile[]>>;
  treatmentCategory?: TreatmentCategory;
  uploadedFiles: PhotoUploadFile[];
}) => {
  const [open, setOpen] = useState(photo.required);

  return (
    <Accordion className="cur-bg-surface-neutral cur-my-4" open={open}>
      <AccordionSummary
        className="cur-border-t-0 cur-p-2"
        onClick={() => setOpen(!open)}
      >
        <div className="cur-flex cur-items-center cur-justify-between">
          <Typography data-testid="photo-accordion-title" weight="bold">
            {`${index + 1} .  ${photo.title}`}
          </Typography>
          <Typography
            data-testid="photo-accordion-required-or-optional"
            size="meta"
          >
            {photo.required ? 'Required' : 'Optional'}
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionContent className="cur-border-solid cur-border-t cur-border-t-curology-primary cur-m-0 cur-p-4">
        {photo.description && (
          <Typography
            className="cur-pb-4"
            data-testid="photo-accordion-description"
          >
            <span
              dangerouslySetInnerHTML={{ __html: photo.description }}
            ></span>
          </Typography>
        )}
        <div className="cur-flex">
          <ExamplePhoto photo={photo} />
          <PhotoUploader
            brand={brand}
            photo={photo}
            question={question}
            requiredPhotoTypes={requiredPhotoTypes}
            setSubmitButtonDisabled={setSubmitButtonDisabled}
            setUploadedFiles={setUploadedFiles}
            treatmentCategory={treatmentCategory}
            uploadedFiles={uploadedFiles}
          />
        </div>
        {photo.modalLinkText && (
          <div className="cur-text-center cur-mt-4">
            <Link
              data-testid="photo-accordion-modal-link"
              href=""
              onClick={(event) => {
                setShowPhotoIdModal(true);
                event.preventDefault();
              }}
              size="cta"
            >
              {photo.modalLinkText}
            </Link>
          </div>
        )}
      </AccordionContent>
    </Accordion>
  );
};

export default PhotoUploadAccordion;
